import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { env } from '../config/environment';
import { APIError, UnauthorizedError } from '../types/api';

class APIClient {
  private client: AxiosInstance;
  private authToken: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: env.API_BASE_URL,
      timeout: env.API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
    this.loadAuthToken();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        if (this.authToken && config.headers) {
          config.headers.Authorization = `Bearer ${this.authToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        const originalRequest = error.config;
        
        if (error.response) {
          const { status, data } = error.response;
          
          if (status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            
            try {
              // Try to refresh the token
              await this.refreshToken();
              
              // Retry the original request with the new token
              originalRequest.headers.Authorization = `Bearer ${this.authToken}`;
              return this.client(originalRequest);
            } catch (refreshError) {
              // If refresh fails, surface the unauthorized error
              console.error('Token refresh failed:', refreshError);
              throw new UnauthorizedError(data.message || 'Unauthorized - token refresh failed');
            }
          }
          
          throw new APIError(
            data.message || data.error || 'An error occurred',
            status,
            data.error
          );
        } else if (error.request) {
          throw new APIError('Network error - please check your connection', 0);
        } else {
          throw new APIError('Request setup error', 0);
        }
      }
    );
  }

  private loadAuthToken() {
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        this.authToken = token;
      }
    } catch (error) {
      console.warn('Failed to load auth token from localStorage:', error);
    }
  }

  setAuthToken(token: string) {
    this.authToken = token;
    try {
      localStorage.setItem('auth_token', token);
    } catch (error) {
      console.warn('Failed to save auth token to localStorage:', error);
    }
  }

  clearAuthToken() {
    this.authToken = null;
    try {
      // Only clear the raw access token here. Preserve other auth state.
      localStorage.removeItem('auth_token');
    } catch (error) {
      console.warn('Failed to clear auth data from localStorage:', error);
    }
  }

  getAuthToken(): string | null {
    return this.authToken;
  }

  isAuthenticated(): boolean {
    return Boolean(this.authToken);
  }

  private async refreshToken(): Promise<void> {
    try {
      // Get refresh token and user email from localStorage
      const authTokens = localStorage.getItem('auth_tokens');
      const userProfile = localStorage.getItem('user_profile');
      
      if (!authTokens || !userProfile) {
        throw new Error('No refresh token or user profile available');
      }

      const tokens = JSON.parse(authTokens);
      const user = JSON.parse(userProfile);
      
      if (!tokens.refreshToken) {
        throw new Error('No refresh token available');
      }

      // Call refresh endpoint
      const response = await this.client.post('/auth/refresh', {
        refreshToken: tokens.refreshToken,
        username: user.email, // Needed for Cognito SECRET_HASH
      });

      if (response.data.success && response.data.data) {
        const newTokens = {
          ...tokens,
          accessToken: response.data.data.accessToken,
          idToken: response.data.data.idToken,
        };

        // Update stored tokens
        this.setAuthToken(newTokens.accessToken);
        localStorage.setItem('auth_tokens', JSON.stringify(newTokens));

        console.log('🔄 [APIClient] Token refreshed successfully');
      } else {
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      console.error('🔄 [APIClient] Token refresh error:', error);
      throw error;
    }
  }

  // HTTP methods
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  // File upload helper
  async uploadFile(url: string, file: File, onProgress?: (progress: number) => void): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);

    await this.client.put(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  }

  // Raw client access for special cases
  getClient(): AxiosInstance {
    return this.client;
  }
}

// Singleton instance
export const apiClient = new APIClient();
